/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useMemo, useState } from 'react';

import {
    ArrowBackIosNew as ArrowBackIcon,
    ArrowForwardIos as ArrowForwardIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Menu as MenuIcon,
    MoreHoriz as MoreHorizIcon
} from '@mui/icons-material';
import {
    AppBar as MuiAppBar,
    AppBarProps as MuiAppBarProps,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    Toolbar,
    Typography
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styledComponent from 'styled-components';

import { WhiteboardContext } from './context/WhiteboardContext';
import { WhiteboardSessionContext } from './context/WhiteboardSessionContext';
import { useWhiteboardSubtitle, useWhiteboardTitle } from './hooks';
import { SessionTypes } from './types';
import WhiteboardSidebarContent from './WhiteboardSidebarContent';
import WhiteboardToolbar from './WhiteboardToolbar';
import { checkPermission } from '../../utils/permission';
import { ActionMenu, ActionMenuItem } from '../common/table/ActionMenu';
import { UserRoles } from '../roster/types';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    marginLeft: `-${drawerWidth}px`,
    height: '100vh',
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0
    })
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'space-between'
}));

const StyledDrawer = styledComponent(Drawer)`
    background-color: ${(props) => props.theme.colors.backgroundLight};
    flex-shrink: 0;
    
    & .MuiPaper-root {
        background: transparent;
        box-sizing: 'border-box'
    }
`;

const Subtitle = styled(Typography)`
    word-break: break-all;
`;

export const WhiteboardLayout: React.FC<{
    children: React.ReactNode;
    onNextPage?: () => void;
    onPrevPage?: () => void;
}> = (props) => {
    const { t } = useTranslation('whiteboard');
    const { userInfo } = useContext(WhiteboardContext);
    const { setShowSettingsDialog, sessionOptions, whiteboardSessionState } = useContext(WhiteboardSessionContext);
    const { session } = whiteboardSessionState;

    const theme = useTheme();
    const title = useWhiteboardTitle();
    const subtitle = useWhiteboardSubtitle();

    const { onNextPage, onPrevPage } = props;

    const [drawerOpen, setDrawerOpen] = useState(false);
    const showDrawer = sessionOptions.showPageList && drawerOpen;

    const showWhiteboardActions = checkPermission(UserRoles.Assistant, userInfo?.role || UserRoles.Student);

    const showNavigation = useMemo(() => {
        if (session?.type === SessionTypes.Individual) {
            return false;
        }

        return (!!onNextPage || !!onPrevPage) && sessionOptions.allowPageNavigation;
    }, [onNextPage, onPrevPage, session?.type, sessionOptions.allowPageNavigation]);

    const actions: ActionMenuItem[] = [{ name: t('common:settings'), onClick: () => setShowSettingsDialog(true) }];

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <StyledDrawer
                sx={{
                    width: drawerWidth,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth
                    }
                }}
                variant="persistent"
                anchor="left"
                open={showDrawer}
            >
                <DrawerHeader>
                    <Typography variant="body1" justifyContent="left">
                        {t('whiteboardPages')}
                    </Typography>
                    <IconButton onClick={() => setDrawerOpen(false)}>
                        {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <WhiteboardSidebarContent />
            </StyledDrawer>

            <Main open={showDrawer}>
                <AppBar position="relative" open={showDrawer}>
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label={t('openDrawer')}
                            onClick={() => setDrawerOpen(true)}
                            edge="start"
                            sx={{ mr: 2, ...(showDrawer && { display: 'none' }) }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Box sx={{ flexGrow: 1 }}>
                            {title}

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {showNavigation && (
                                    <IconButton
                                        color="inherit"
                                        aria-label={t('navigation.previousPage')}
                                        onClick={onPrevPage}
                                        size="small"
                                        disabled={!onPrevPage}
                                    >
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {subtitle && (
                                    <Subtitle sx={showNavigation ? { ml: 1, mr: 1 } : null}>{subtitle}</Subtitle>
                                )}
                                {showNavigation && (
                                    <IconButton
                                        color="inherit"
                                        aria-label={t('navigation.nextPage')}
                                        onClick={onNextPage}
                                        size="small"
                                        disabled={!onNextPage}
                                    >
                                        <ArrowForwardIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        <WhiteboardToolbar />

                        {showWhiteboardActions && (
                            <ActionMenu uniqueId="actions" title={t('sessionActions')} actions={actions}>
                                <MoreHorizIcon sx={{ color: 'white' }} fontSize="large" />
                            </ActionMenu>
                        )}
                    </Toolbar>
                </AppBar>
                {props.children}
            </Main>
        </Box>
    );
};
